/**
 * The site header on /marketplace scrolls away with the page and comes back as
 * the shopper scrolls up, at the rate they scroll (owner request, 2026-10-19).
 * The marketplace's sticky filter bar rides under it, so the bar moves up into
 * the room the header leaves. Everywhere else the header is as it was. This is
 * the half that needs no React and no DOM globals, so node:test can drive it;
 * src/hooks/use-site-header-autohide.ts binds it to the page.
 *
 * HOW. The header stays `sticky top-0` (src/components/layout/header.tsx) and
 * this decides how many pixels of it sit above the top edge: its sticky `top`
 * becomes minus that many. Sticky positioning never lifts an element above
 * where the page itself would put it, so near the top of the page the header
 * can only scroll away like a plain one, and no gap can open above it. The
 * filter bar's sticky top subtracts the same number (--site-header-hidden),
 * and both hear the same publish in the same task, so they move in the same
 * frame and the bar never overlaps the header or leaves a gap under it.
 *
 * ONLY THE SHOPPER'S OWN SCROLLING MOVES IT. A scroll event counts while a
 * finger is on the screen or the page's scrollbar is held, shortly after a
 * wheel turn or a key press outside a text field, and as part of an unbroken
 * run of scroll events one of those started (a fling's momentum, a smooth
 * scroll's tail). Anything else leaves the header where it is: a Back that
 * restores the grid position (so the header shows on arrival, as on every
 * other page), the browser pulling the page up when a search shrinks the
 * grid, and the page's own scrolls.
 *
 * SETTLING. Once the shopper has let go and the page is still, a header left
 * partly on screen finishes whichever move it was nearer: half or more hidden
 * slides away, less slides back. A nudge does not open it, and it never stays
 * cut in half. Near the very top the page decides instead (a header 30px into
 * a page scrolled 30px is just a plain header), so there is nothing to settle.
 *
 * PINS, HOLDS AND REVEALS. Pinned (one of the header's own menus is open, or
 * focus is in it), it comes fully in and stays: at once for focus, which the
 * browser scrolls to straight away, slid for a menu. A hold (one of the
 * filter bar's menus is open) freezes it wherever it is, settles included, so
 * the menu stays attached to its button. A reveal slides it fully in once, after which the
 * shopper's scrolling takes over again; the cart asks for one on a real add,
 * so the count changing and the add-to-cart star landing are both on screen.
 */

/** How soon after a wheel turn or key press a scroll event still counts as the shopper's. */
export const INPUT_WINDOW_MS = 250;
/** How close together scroll events must be to count as one run with a counted event. */
export const CHAIN_MS = 250;
/**
 * How long the page must be still, with nothing held, before a part-hidden
 * header settles. No shorter than CHAIN_MS: a fling's last events come
 * sparse, and one landing after a settle had begun would still count as the
 * shopper's, stop the slide and start the wait over.
 */
export const SETTLE_MS = CHAIN_MS;
/** How long a settle, a pin or a reveal takes to slide the header the rest of the way. */
export const SLIDE_MS = 200;

const AUTOHIDE_PATHS: ReadonlySet<string> = new Set(['/marketplace']);

/** Whether the header hides on this page: the marketplace itself, and nothing under or beside it. */
export function siteHeaderAutoHides(pathname: string | null | undefined): boolean {
  return typeof pathname === 'string' && AUTOHIDE_PATHS.has(pathname);
}

/**
 * Something the shopper did that can scroll the page. `hold` and `release` are
 * a press and release on the page's own scrollbar.
 */
export type ScrollInput = 'wheel' | 'key' | 'touchstart' | 'touchend' | 'hold' | 'release';

export type HeaderMeasure = {
  /** The page's scroll position, and the furthest it can scroll. */
  scroll(): { y: number; maxY: number };
  /** The header's full height, border included: how far it can hide. */
  height(): number;
};

export type SiteHeaderAutoHideOptions = {
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  requestFrame?: (fn: () => void) => unknown;
  cancelFrame?: (handle: unknown) => void;
  /** Read at each slide, so a change to the system setting applies at once. */
  motionAllowed?: () => boolean;
};

export type SiteHeaderAutoHide = {
  /** Hears the header's hidden pixels, whole, once per change. */
  subscribe(listener: (hidden: number) => void): () => void;
  getHidden(): number;
  /** Start following the page, header fully shown. */
  attach(measure: HeaderMeasure): void;
  /** Stop, and hand the header back fully shown. */
  detach(): void;
  /** The page's scroll event. */
  scrolled(): void;
  input(kind: ScrollInput): void;
  /** The window's resize event. */
  resized(): void;
  /**
   * Keep the header fully in while pinned. `instant` puts it in at once rather
   * than sliding it: keyboard focus arriving in a hidden header is scrolled
   * into view by the browser straight after the focus event, and a header
   * still off screen then jumps the page to the top.
   */
  setPinned(pinned: boolean, options?: { instant?: boolean }): void;
  /** Freeze the header while `isHeld()` is true. Returns the release. */
  addHold(isHeld: () => boolean): () => void;
  /** Slide the header fully in, if it is hiding. */
  reveal(): void;
};

const easeOutCubic = (p: number) => 1 - Math.pow(1 - p, 3);
const clamp = (value: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, value));

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function defaultRequestFrame(fn: () => void): unknown {
  return typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => fn()) : setTimeout(fn, 16);
}

function defaultCancelFrame(handle: unknown): void {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle as number);
  else clearTimeout(handle as ReturnType<typeof setTimeout>);
}

function defaultMotionAllowed(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function createSiteHeaderAutoHide(options: SiteHeaderAutoHideOptions = {}): SiteHeaderAutoHide {
  const now = options.now ?? defaultNow;
  const setTimer = options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = options.clearTimer ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const requestFrame = options.requestFrame ?? defaultRequestFrame;
  const cancelFrame = options.cancelFrame ?? defaultCancelFrame;
  const motionAllowed = options.motionAllowed ?? defaultMotionAllowed;

  const listeners = new Set<(hidden: number) => void>();
  const holds = new Set<() => boolean>();
  let measure: HeaderMeasure | null = null;
  /** Pixels of the header above the top edge, unrounded. */
  let hidden = 0;
  /** What the listeners last heard. */
  let published = 0;
  /** The scroll position at the last scroll event, kept inside the page. */
  let lastY = 0;
  let lastInputAt = -Infinity;
  let lastCountedAt = -Infinity;
  let touching = false;
  let scrollbarHeld = false;
  let pinned = false;
  let settleTimer: unknown = null;
  let slideFrame: unknown = null;

  // Inside the page: an iPhone reports positions past either end while the
  // page rubber-bands, and the spring back is not the shopper scrolling.
  const readY = (m: HeaderMeasure) => {
    const { y, maxY } = m.scroll();
    return clamp(y, 0, Math.max(0, maxY));
  };
  // All of the header, or as much of it as the page has scrolled past.
  const mostHidden = (m: HeaderMeasure) => Math.max(0, Math.min(m.height(), lastY));
  const isHeld = () => {
    for (const held of Array.from(holds)) if (held()) return true;
    return false;
  };

  const publish = () => {
    const whole = Math.round(hidden);
    if (whole === published) return;
    published = whole;
    for (const listener of Array.from(listeners)) listener(whole);
  };

  const stopSlide = () => {
    if (slideFrame === null) return;
    cancelFrame(slideFrame);
    slideFrame = null;
  };
  const stopSettle = () => {
    if (settleTimer === null) return;
    clearTimer(settleTimer);
    settleTimer = null;
  };

  const slideTo = (target: number, yieldToHold = false) => {
    stopSlide();
    const m = measure;
    if (!m) return;
    if (!motionAllowed()) {
      hidden = clamp(target, 0, mostHidden(m));
      publish();
      return;
    }
    const from = hidden;
    const start = now();
    const step = () => {
      slideFrame = null;
      const live = measure;
      if (!live) return;
      // A settle gives way to an open filter menu, as scrolling does, so the
      // menu's button does not move under it. The settle looks again later.
      if (yieldToHold && isHeld()) {
        armSettle();
        return;
      }
      const p = Math.min(1, (now() - start) / SLIDE_MS);
      // Clamped every frame: the page can move under a slide (the marketplace
      // scrolls itself up to the results while the header comes back in).
      hidden = clamp(from + (target - from) * easeOutCubic(p), 0, mostHidden(live));
      publish();
      if (p < 1) slideFrame = requestFrame(step);
    };
    slideFrame = requestFrame(step);
  };

  // Slide fully in, and stop anything that would take it the other way.
  const slideIn = () => {
    stopSettle();
    if (hidden > 0) slideTo(0);
    else stopSlide();
  };
  // In at once, with nothing left pending.
  const showNow = () => {
    stopSettle();
    stopSlide();
    hidden = 0;
    publish();
  };

  const settle = () => {
    settleTimer = null;
    const m = measure;
    // A finger or the scrollbar still down settles on its own release; a
    // running slide is already heading for a resting place.
    if (!m || pinned || touching || scrollbarHeld || slideFrame !== null) return;
    const most = mostHidden(m);
    if (hidden <= 0 || hidden >= most) return;
    // Under an open filter menu, look again shortly rather than move the menu.
    if (isHeld()) {
      armSettle();
      return;
    }
    slideTo(hidden >= most / 2 ? most : 0, true);
  };
  const armSettle = () => {
    stopSettle();
    settleTimer = setTimer(settle, SETTLE_MS);
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getHidden: () => published,
    attach(m) {
      stopSlide();
      stopSettle();
      measure = m;
      hidden = 0;
      lastY = readY(m);
      lastInputAt = -Infinity;
      lastCountedAt = -Infinity;
      touching = false;
      scrollbarHeld = false;
      publish();
    },
    detach() {
      stopSlide();
      stopSettle();
      measure = null;
      hidden = 0;
      touching = false;
      scrollbarHeld = false;
      publish();
    },
    scrolled() {
      const m = measure;
      if (!m) return;
      const t = now();
      const y = readY(m);
      const dy = y - lastY;
      lastY = y;
      const counted = touching || scrollbarHeld
        || t - lastInputAt <= INPUT_WINDOW_MS
        || t - lastCountedAt <= CHAIN_MS;
      if (counted) lastCountedAt = t;
      if (counted && dy !== 0 && !pinned && !isHeld()) {
        // The shopper's scroll wins over any slide in progress.
        stopSlide();
        hidden += dy;
      }
      hidden = clamp(hidden, 0, mostHidden(m));
      publish();
      if (counted) armSettle();
    },
    input(kind) {
      if (!measure) return;
      switch (kind) {
        case 'wheel':
        case 'key':
          lastInputAt = now();
          scrollbarHeld = false;
          break;
        case 'touchstart':
          touching = true;
          scrollbarHeld = false;
          break;
        case 'touchend':
          touching = false;
          armSettle();
          break;
        case 'hold':
          scrollbarHeld = true;
          break;
        case 'release':
          // A browser may never report the release after a scrollbar drag, so
          // any other input (above) also ends the hold.
          if (!scrollbarHeld) break;
          scrollbarHeld = false;
          armSettle();
          break;
      }
    },
    resized() {
      const m = measure;
      if (!m) return;
      lastY = readY(m);
      hidden = clamp(hidden, 0, mostHidden(m));
      publish();
    },
    setPinned(next, { instant = false } = {}) {
      pinned = next;
      if (!next || !measure) return;
      if (instant) showNow();
      else slideIn();
    },
    addHold(held) {
      holds.add(held);
      return () => {
        holds.delete(held);
      };
    },
    reveal() {
      if (measure) slideIn();
    },
  };
}

const TEXTLESS_INPUTS: ReadonlySet<string> = new Set([
  'button', 'submit', 'reset', 'checkbox', 'radio', 'image', 'file', 'color', 'hidden',
]);

/**
 * Whether a key press here types rather than scrolls: a text-like input, a
 * textarea, a select or anything contenteditable. Typing into the filter bar's
 * search reshapes the grid, and the browser pulling the page up after it is
 * not the shopper scrolling.
 */
export function isTextEntry(target: unknown): boolean {
  if (!target || typeof target !== 'object') return false;
  const el = target as { tagName?: unknown; type?: unknown; isContentEditable?: unknown };
  if (el.isContentEditable === true) return true;
  const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : '';
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  const type = typeof el.type === 'string' && el.type ? el.type.toLowerCase() : 'text';
  return !TEXTLESS_INPUTS.has(type);
}

/**
 * Whether a press landed on the page's own scrollbar, which sits outside the
 * root element's client box. An overlay scrollbar takes no room and is never
 * pressed this way, so that edge stays page.
 */
export function onPageScrollbar(
  point: { clientX: number; clientY: number },
  viewport: { clientWidth: number; clientHeight: number },
): boolean {
  return point.clientX >= viewport.clientWidth || point.clientY >= viewport.clientHeight;
}

/**
 * The one controller the app uses. The Header attaches it on the marketplace;
 * the filter bar rides on what it publishes, and the cart asks it for a
 * reveal. Off the marketplace it is detached and every call is a no-op.
 */
export const siteHeaderAutoHide = createSiteHeaderAutoHide();
/** Slide the header fully in, if it is hiding. Called by cart-context on a real add. */
export const revealSiteHeader = siteHeaderAutoHide.reveal;
/** Freeze the header while `isHeld()` is true; returns the release. */
export const holdSiteHeader = siteHeaderAutoHide.addHold;
